import { useCallback, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { MaskWords } from "../../kit.jsx";
import { AAROH_ALBUM } from "../../data/album.js";
import { EASE_OUT } from "../../kit.jsx";
import { AlbumImg, Lightbox } from "./media.jsx";

const CATEGORIES = [
  ["all", "Everything"],
  ["crowd", "The hall"],
  ["building", "Building"],
  ["presenting", "Presenting"],
  ["awards", "Awards"],
  ["teams", "Teams"],
  ["candid", "Candid"],
  ["venue", "Venue"],
  ["details", "Details"],
];

const FIRST_VIEW = 32;

// A filterable photo gallery with a lightbox. AAROH uses it with its album; other programmes pass their own photos.
export function PhotoGallery({ photos: source, categories = CATEGORIES, title, intro, link, linkLabel, id = "pg-gal" }) {
  const reduceMotion = useReducedMotion();
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(null);

  const photos = useMemo(() => [...source].sort((a, b) => a.rank - b.rank), [source]);
  const filtered = filter === "all" ? photos : photos.filter((photo) => photo.category === filter);
  const shown = filter === "all" && !expanded ? filtered.slice(0, FIRST_VIEW) : filtered;
  const counts = useMemo(
    () => Object.fromEntries(categories.map(([key]) => [key, key === "all" ? photos.length : photos.filter((photo) => photo.category === key).length])),
    [photos, categories],
  );

  const step = useCallback((delta) => setOpen((current) => (current === null ? null : (current + delta + filtered.length) % filtered.length)), [filtered.length]);
  const close = useCallback(() => setOpen(null), []);

  return (
    <section className="pg-gal" aria-labelledby={`${id}-title`}>
      <header className="pg-gal__head">
        <MaskWords id={`${id}-title`} text={title} />
        <div>
          <p>{intro(photos.length)}</p>
          {link ? <a className="gh-link" href={link} target="_blank" rel="noreferrer">{linkLabel} <ArrowUpRight weight="bold" aria-hidden="true" /></a> : null}
        </div>
      </header>

      <div className="pg-gal__chips" role="group" aria-label="Filter photos by part of the festival">
        {categories.filter(([key]) => key === "all" || counts[key] > 0).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`pg-gal__chip ${filter === key ? "is-on" : ""}`}
            aria-pressed={filter === key}
            onClick={() => { setFilter(key); setOpen(null); }}
          >
            {label}<span>{counts[key]}</span>
          </button>
        ))}
      </div>

      <motion.ul className="pg-gal__grid" layout={!reduceMotion}>
        <AnimatePresence mode="popLayout" initial={false}>
          {shown.map((photo, index) => {
            const ratio = photo.width / photo.height;
            return (
              <motion.li
                key={photo.id}
                layout={!reduceMotion}
                style={{ flexGrow: ratio, flexBasis: `calc(var(--row) * ${ratio.toFixed(3)})` }}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduceMotion ? undefined : { opacity: 0, scale: 0.96, transition: { duration: 0.2 } }}
                transition={{ duration: 0.55, ease: EASE_OUT }}
              >
                <button type="button" onClick={() => setOpen(index)} aria-label={`Open photo: ${photo.caption}`}>
                  <span className="pg-gal__img" style={{ aspectRatio: `${photo.width} / ${photo.height}` }}>
                    <AlbumImg photo={photo} size="small" />
                  </span>
                </button>
                <p>{photo.caption}</p>
              </motion.li>
            );
          })}
        </AnimatePresence>
        <li className="pg-gal__spacer" aria-hidden="true" />
      </motion.ul>

      {filter === "all" && !expanded && filtered.length > FIRST_VIEW && (
        <button type="button" className="gh-btn gh-btn--ghost-dark pg-gal__more" onClick={() => setExpanded(true)}>
          Show all {filtered.length} photos
        </button>
      )}

      <AnimatePresence>
        {open !== null && <Lightbox key="lightbox" photos={filtered} index={open} onClose={close} onStep={step} />}
      </AnimatePresence>
    </section>
  );
}

export default function AarohGallery({ album }) {
  return (
    <PhotoGallery
      photos={AAROH_ALBUM}
      title="Two days in photos."
      intro={(count) => `${count} photographs from the AAROH album, taken on 1 and 2 August 2026 at IIIT Delhi. Pick a part of the festival, or open any photo to see it large.`}
      link={album}
      linkLabel="The full album on Google Photos"
    />
  );
}
